<template>
  <li class="folder-item">
    <template v-if="homeLayout">
      <ProjectNavigationSection :key="`${folder.id}${folder.parentId}`"
                                :value="`${folder.id}${folder.parentId}`"
                                :title="folder.name"
                                :icon="folder.icon ? folder.icon : ''"
                                @click="onFolderClick"
      >
        <template #content>
          <DocumentsSidebarFolderItemContent :folder="folder"
                                             :depth="depth"
                                             :isCurrentlyViewedFolder="isCurrentlyViewedFolder"
                                             :areChildrenExpanded="areChildrenExpanded"
                                             :loadingChildren="loadingChildren"
                                             :disabled="false"
                                             @load-children="loadChildren"
          >
          </DocumentsSidebarFolderItemContent>
        </template>
      </ProjectNavigationSection>

      <template v-if="areChildrenExpanded">
        <DocumentsSidebarFolderItem v-for="childFolder in sortedChildren"
                                    :key="childFolder.id"
                                    :folder="childFolder"
                                    :depth="depth + 1"
                                    :children-sort="childrenSort"
                                    :disabled="false"
        >
        </DocumentsSidebarFolderItem>
      </template>
    </template>
    <ul v-else>
      <AppDroppableArea :disabled="disabled"
                        :draggable="true"
                        @dragenter="dragenter(folder)"
                        @dragleave="dragleave(folder)"
                        @ondrop="drop($event, folder)"
                        @dragstart="dragstart($event, folder)"
      >
        <app-treeview-item :selected="isCurrentlyViewedFolder"
                           @click="onFolderClick"
        >
          <div class="folder-item__inner-content"
               data-locator="folder-item"
               :style="{ paddingLeft: leftOffset }"
          >
            <app-icon-ds v-if="areChildrenFoldersLoading"
                      class="text-primary"
                      weight="far"
                      icon="spinner-third"
                      spin
            />

            <app-icon v-if="hasFolderChildren && !areChildrenFoldersLoading"
                      size="regular"
                      icon-weight="fas"
                      :icon-name="areDocumentChildrenExpanded ? 'caret-down' : 'caret-right'"
                      class="expand-icon"
                      @click.stop="loadChildren"
            />

            <app-icon size="regular"
                      icon-weight="fas"
                      icon-name="folder"
                      class="folder-icon"
            />

            <AppTooltip :open-delay="500" :disabled="!hasTooltip">
              <template #activator="{ props }">
                <div v-bind="props"
                     ref="folderName"
                     class="folder-name text-truncate"
                >
                  <span
                            :variant="isCurrentlyViewedFolder ? 'small-bold' : 'small-regular'"
                  >
                    {{ folderName }}
                  </span>
                </div>
              </template>
              {{ folderName }}
            </AppTooltip>
          </div>
        </app-treeview-item>
      </AppDroppableArea>
      <template v-if="areDocumentChildrenExpanded">
        <DocumentsSidebarFolderItem v-for="childFolder in sortedChildren"
                                    :key="childFolder.id"
                                    :folder="childFolder"
                                    :depth="depth + 1"
                                    :children-sort="childrenSort"
                                    :disabled="disabled"
                                    :draggable="true"
                                    @drag-enter-folder="dragenter"
                                    @drag-leave-folder="dragleave"
                                    @drop-documents-in-folder="drop"
                                    @dragstart="dragstart"
        />
      </template>
    </ul>
  </li>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapActions, mapState } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { FOLDER_ICON_COLOR } from '@/common/constants'
import { toast } from '@/common/utils/toast'
import projectsFoldersMixin from '@/mixins/projectsFoldersMixin'
import { RECENT_FOLDER, ROOT_FOLDER } from '@/project/documents/constants/special-folders'
import ProjectNavigationSection from '@/project/navigation-drawer/ProjectNavigationSection.vue'
import { ROOMS_ROUTE_NAME, FOLDER_ROUTE_NAME } from '@/router/index'
import {
  GET_ALL_FOLDERS_IN_FOLDER,
  GET_FOLDER_PATH,
  GO_TO_FOLDER_PAGINATED,
  SET_FOLDER_CHILDREN,
  SET_FOLDER_CHILDREN_EXPANDED,
} from '@/store/modules/documents/action_types'
import { DocumentsBreadcrumbActionTypes } from '@/store/modules/documents-breadcrumb/action_types'
import { GET_PROJECTS_FOLDER_BY_ID } from '@/store/modules/projects-folders/action_types'

import DocumentsSidebarFolderItemContent, { type DocumentsSidebarFolder } from './DocumentsSidebarFolderItemContent.vue'

type BreadcrumbFolder = {
  id: number
}

export default defineComponent({
  name: 'DocumentsSidebarFolderItem',
  components: {
    ProjectNavigationSection,
    DocumentsSidebarFolderItemContent,
    AppDroppableArea,
    AppTooltip,
  },
  mixins: [projectsFoldersMixin],
  props: {
    folder: {
      type: Object as PropType<DocumentsSidebarFolder>,
      required: true,
    },
    depth: {
      type: Number,
      default: 0,
    },
    childrenSort: {
      type: Function as PropType<(folderA: DocumentsSidebarFolder, folderB: DocumentsSidebarFolder) => number>,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      areChildrenExpanded: false,
      hasTooltip: false,
      loadingChildren: false,
      FOLDER_ICON_COLOR,
    }
  },
  computed: {
    ...mapState('global', ['homeLayout']),
    ...mapState('documents', ['childrenLoadingFolderIds', 'paginatedDocumentsCurrentFolderId']),
    ...mapState('projectsFolders', ['currentProjectsFolder']),
    ...mapState('documentsBreadcrumb', ['breadcrumb', 'folderNavigationToken']),
    areChildrenFoldersLoading() {
      return this.childrenLoadingFolderIds.includes(this.folder.id)
    },
    findAndExpandCurrentProjectsFolder () {
      let expand = false
      if (this.folder.id === this.currentProjectsFolder.id) {
        expand = true
      } else if (!expand && this.folder.children && this.folder.children.length > 0) {
        if (this.folder.children.find(item => item.id === this.currentProjectsFolder.id)) {
          expand = true
        }
      }
      return expand
    },
    isCurrentlyViewedFolder (): boolean {
      if (this.homeLayout) {
        return this.currentProjectsFolder.id === this.folder.id
      }
      return this.paginatedDocumentsCurrentFolderId === this.folder.id
    },
    leftOffset (): string {
      const LEVEL_OFFSET = 8
      return `${this.depth * LEVEL_OFFSET}px`
    },
    hasFolderChildren (): boolean {
      return this.folder.hasFolderChildren ?? false
    },
    areDocumentChildrenExpanded (): boolean {
      return this.folder.areChildrenExpanded ?? false
    },
    folderName (): string {
      if (this.folder.numbering) {
        return `${this.folder.numbering} ${this.folder.name}`
      }

      return this.folder.name
    },
    sortedChildren (): Array<DocumentsSidebarFolder> {
      if (!this.folder.children) {
        return []
      }

      if (this.homeLayout) {
        return this.folder.children
      }
      return [...this.folder.children].sort(this.childrenSort)
    },
  },
  watch: {
    breadcrumb: {
      handler (breadcrumbFolders: Array<BreadcrumbFolder>) {
        const isFolderInBreadcrumb = breadcrumbFolders.some(
          breadcrumbFolder => breadcrumbFolder.id === this.folder.id,
        )

        if (isFolderInBreadcrumb && !this.areChildrenExpanded) {
          this.areChildrenExpanded = true
        }
      },
      immediate: true,
    },
    // If the current folder changes, then open the corresponding folder in the sidebar
    // For example, by clicking on the grid of folder or subfolder to open it
    currentProjectsFolder () {
      if (this.homeLayout && this.currentProjectsFolder.id === this.folder.id) {
        this.areChildrenExpanded = true
      }
    },
  },
  created () {
    if (this.homeLayout && this.currentProjectsFolder.id) {
      if (this.findAndExpandCurrentProjectsFolder) {
        this.areChildrenExpanded = true
      }
    }
  },
  mounted () {
    (this.$refs['folderName'] as HTMLDivElement)?.addEventListener(
      'mouseenter',
      (event) => {
        const folderNameDivElement = event.target as HTMLDivElement
        this.hasTooltip = folderNameDivElement.offsetWidth < folderNameDivElement.scrollWidth
      },
    )
  },
  methods: {
    ...mapActions('projectsFolders', [GET_PROJECTS_FOLDER_BY_ID]),
    ...mapActions('documents', [GET_FOLDER_PATH, GO_TO_FOLDER_PAGINATED, SET_FOLDER_CHILDREN_EXPANDED, SET_FOLDER_CHILDREN, GET_ALL_FOLDERS_IN_FOLDER]),
    ...mapActions('documentsBreadcrumb', [DocumentsBreadcrumbActionTypes.SET_BREADCRUMB_ITEMS]),
    async loadChildren () {
      if (this.homeLayout) {
        if (!this.areChildrenExpanded) {
          if (this.currentProjectsFolder.id === this.folder.id) {
            this.areChildrenExpanded = true
          } else {
            this.loadingChildren = true
            try {
              await this.GET_PROJECTS_FOLDER_BY_ID({
                folderId: this.folder.id,
                silent: true,
              })
              this.areChildrenExpanded = true
            } catch (error) {
              toast.error(this.$t('project.documents.dialogs.DocumentsNewFolderDialog.getFolderError', { name: this.folder.name }))
            } finally {
              this.loadingChildren = false
            }
          }
        } else {
          this.areChildrenExpanded = false
        }
      } else {
        if (this.areDocumentChildrenExpanded) {
          this.SET_FOLDER_CHILDREN_EXPANDED({
            folderId: this.folder.id,
            areChildrenExpanded: false,
          })
          return
        }

        this.loadDocumentChildren()
      }
    },
    async loadDocumentChildren() {
      await this.GET_ALL_FOLDERS_IN_FOLDER(this.folder.id)
    },
    async onFolderClick () {
      if (this.homeLayout) {
        this.handleHomeLayoutFolderClick()
      } else {
        this.GO_TO_FOLDER_PAGINATED(this.folder.id)

        const folderPath = await this.GET_FOLDER_PATH(this.folder.id)
        const token = this.folderNavigationToken
        if (token !== this.folderNavigationToken) return // STOP : navigation changed
        this.SET_BREADCRUMB_ITEMS(
          [{
            id: ROOT_FOLDER.id,
            name: this.$t('common.documents.rootFolder'),
          }].concat(folderPath),
        )
      }
    },
    handleHomeLayoutFolderClick () {
      if (this.currentProjectsFolder.id === this.folder.id) return

      if (this.folder.id === RECENT_FOLDER.id) {
        this.handleRecentFolderClick()
      } else if (this.folder.id === ROOT_FOLDER.id) {
        this.handleRootFolderClick()
      } else {
        this.navigateToProjectsFolder()
        this.areChildrenExpanded = true
      }
    },
    handleRecentFolderClick () {
      if (this.$route.path !== '/folder/recent') {
        this.$router.push({
          name: FOLDER_ROUTE_NAME,
          params: {
            folderId: 'recent',
          },
        })
      }
    },
    handleRootFolderClick () {
      if (this.$route.path !== '/') {
        this.$router.push({ name: ROOMS_ROUTE_NAME })
      }
    },
    dragenter (folder: DocumentsSidebarFolder) {
      this.$emit('drag-enter-folder', folder)
    },
    dragleave (folder: DocumentsSidebarFolder) {
      this.$emit('drag-leave-folder', folder)
    },
    dragstart ($event: DragEvent, folder: DocumentsSidebarFolder) {
      this.$emit('dragstart', $event, folder)
    },
    drop ($event: DragEvent, folder: DocumentsSidebarFolder) {
      this.$emit('drop-documents-in-folder', $event, folder)
    },
  },
})
</script>

<style lang="scss" scoped>
.folder-item {
  cursor: pointer;
  list-style-type: none;

  ul {
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__inner-content {
    display: grid;
    align-items: end;
    grid-template-columns: 24px auto 1fr;
    grid-template-areas: "expand-icon folder-icon folder-name";
    column-gap: 8px;
    cursor: pointer;
  }

  .expand-icon {
    grid-area: expand-icon;
    color: rgb(var(--v-theme-grey-600));
    border-radius: 4px;
    width: 24px;

    &:hover {
      background: rgb(var(--v-theme-grey-100));
    }
  }

  .folder-icon {
    grid-area: folder-icon;
    color: #7BA9F9
  }

  .folder-name {
    grid-area: folder-name;
  }
}
</style>
